/**
 * Catalogue des alertes.
 *
 * Une alerte n'est jamais stockée : elle est déduite à chaque lecture de l'état et de la
 * stratégie de l'équipe. Elle apparaît donc dès que le manque existe et disparaît dès
 * qu'il est comblé, sans risque de désynchronisation avec les données.
 *
 * Quatre niveaux, du plus grave au plus anodin :
 *  - `error`   : ce qui empêchera l'entreprise de fonctionner au calcul du mois
 *  - `warning` : ce qui la pénalisera sans la bloquer
 *  - `notice`  : ce qui n'a pas d'effet sur le jeu mais reste attendu de l'équipe
 *  - `info`    : ce qu'il faut simplement avoir en tête
 */
export const SEVERITIES = {
  error: { rank: 0, label: 'Bloquant', color: 'error', icon: 'mdi-alert-circle-outline' },
  warning: { rank: 1, label: 'Attention', color: 'warning', icon: 'mdi-alert-outline' },
  notice: { rank: 2, label: 'À surveiller', color: 'yellow', icon: 'mdi-clock-alert-outline' },
  info: { rank: 3, label: 'Information', color: 'info', icon: 'mdi-information-outline' },
}

/**
 * Types d'alerte.
 *
 * `route` est la page à ouvrir pour traiter l'alerte, et `description` explique à
 * l'administrateur ce qu'il coupe s'il désactive le type.
 */
export const ALERT_TYPES = {
  missingBuilding: {
    severity: 'warning', title: 'Locaux manquants', route: '/Buildings',
    description: 'Des collaborateurs n\'ont pas de place dans les locaux de l\'équipe.',
  },
  missingStuff: {
    severity: 'warning', title: 'Équipements manquants', route: '/Stuffs',
    description: 'Des collaborateurs n\'ont pas de poste équipé, détaillés par métier.',
  },
  missingTool: {
    severity: 'warning', title: 'Outils manquants', route: '/Tools',
    description: 'Un métier ou un contrat exige un outil que l\'équipe ne possède pas. Les collaborateurs concernés sont regroupés par métier.',
  },
  noOffer: {
    severity: 'warning', title: 'Aucune offre', route: '/Offers',
    description: 'L\'équipe ne propose aucune offre à la vente.',
  },
  noSupplier: {
    severity: 'warning', title: 'Aucun fournisseur', route: '/Suppliers',
    description: 'Aucun fournisseur n\'est retenu pour le mois.',
  },
  noManager: {
    severity: 'warning', title: 'Encadrement insuffisant', route: '/Staff',
    description: 'L\'encadrement est en dessous du ratio optimal paramétré.',
  },
  contractAtRisk: {
    severity: 'warning', title: 'Contrat en risque', route: '/Contracts',
    description: 'Les contrats signés exigent un métier ou une compétence que l\'équipe n\'a pas. Le volume d\'heures, lui, relève des deux alertes suivantes.',
  },
  overbookedHours: {
    severity: 'warning', title: 'Trop de contrats', route: '/Contracts',
    description: 'Les contrats signés réclament plus de 10 % d\'heures de plus que la production disponible.',
  },
  idleCapacity: {
    severity: 'notice', title: 'Effectif sous-employé', route: '/Staff',
    description: 'La production disponible dépasse de plus de 10 % les heures des contrats signés.',
  },
  contractFailed: {
    severity: 'error', title: 'Contrats non réalisés', route: '/Contracts',
    description: 'Des contrats n\'ont pas pu être honorés au dernier mois calculé.',
  },
  staffIdle: {
    severity: 'error', title: 'Collaborateurs à l\'arrêt', route: '/Staff',
    description: 'Des collaborateurs n\'ont pas pu travailler au dernier mois calculé.',
  },
  negativeResult: {
    severity: 'error', title: 'Résultat négatif', route: '/Finance',
    description: 'Le résultat du dernier mois calculé est dans le rouge.',
  },
  negativeTreasury: {
    severity: 'error', title: 'Trésorerie négative', route: '/Finance',
    description: 'La trésorerie du dernier mois calculé est dans le rouge.',
  },
  runningLoan: {
    severity: 'info', title: 'Emprunt en cours', route: '/Bank',
    description: 'Un emprunt reste à rembourser, avec sa prochaine échéance.',
  },
  missingDepot: {
    severity: 'notice', title: 'Dépôt attendu', route: '/Depot',
    description: 'Une zone de dépôt ouverte n\'a rien reçu de l\'équipe ou d\'un participant.',
  },
  staffStatus: {
    severity: 'notice', title: 'Statut de collaborateur', route: '/Staff',
    description: 'Des collaborateurs portent un statut du catalogue : démission, maladie, formation...',
  },
  contractStatus: {
    severity: 'notice', title: 'Statut de contrat', route: '/Contracts',
    description: 'Des contrats portent un statut du catalogue : litige, retard de paiement...',
  },
  defaultCompanyName: {
    severity: 'info', title: 'Entreprise sans nom', route: '/Company',
    description: 'L\'équipe n\'a pas encore choisi le nom de son entreprise.',
  },
  missingCompanyLogo: {
    severity: 'info', title: 'Entreprise sans logo', route: '/Company',
    description: 'L\'équipe n\'a pas encore choisi de logo.',
  },
}

/** Ordre d'affichage : le plus grave d'abord, puis l'ordre du catalogue. */
const TYPE_ORDER = Object.keys(ALERT_TYPES)

export const ALERT_TYPE_KEYS = TYPE_ORDER

/**
 * Tous les types, du plus grave au plus anodin.
 *
 * Un classement thématique supposerait un découpage qui n'a rien d'évident — un outil
 * manquant relève autant du matériel que des contrats. La gravité, elle, est déjà portée
 * par le catalogue et se lit d'un coup d'œil à la couleur.
 */
export function alertTypesBySeverity() {
  return TYPE_ORDER
    .map(key => ({ key, ...ALERT_TYPES[key] }))
    .sort((a, b) => {
      const severity = SEVERITIES[a.severity].rank - SEVERITIES[b.severity].rank
      if (severity !== 0) return severity
      return TYPE_ORDER.indexOf(a.key) - TYPE_ORDER.indexOf(b.key)
    })
}

/**
 * Un type est actif tant qu'il n'a pas été explicitement désactivé.
 *
 * Les réglages ne contiennent donc que les exceptions : un monde créé avant cette option,
 * ou un type ajouté plus tard, reste actif sans qu'aucune conversion soit nécessaire.
 */
export function isAlertEnabled(settings, type) {
  return !settings || settings[type] !== false
}

export function sortAlerts(alerts) {
  return [...alerts].sort((a, b) => {
    const severity = SEVERITIES[a.severity].rank - SEVERITIES[b.severity].rank
    if (severity !== 0) return severity

    const type = TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type)
    if (type !== 0) return type

    return (a.teamName || '').localeCompare(b.teamName || '')
  })
}
